import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";

export default function GestionEventos() {
  const navigate = useNavigate();

  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [esEdicion, setEsEdicion] = useState(false);

  const [alerta, setAlerta] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success",
  });

  const [modalEliminar, setModalEliminar] = useState({
    mostrar: false,
    id: null,
    titulo: "",
  });

  // Estado del formulario
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    tipo_evento: "",
    url_pdf: "",
    url_externa: "",
  });

  // Estados para Cloudinary (imagen_portada)
  const [imagenFile, setImagenFile] = useState(null);
  const [imagenPreview, setImagenPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    cargarEventos();
  }, []);

  const cargarEventos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/eventosadmin");
      setEventos(response.data.eventos || []);
    } catch (err) {
      console.error("Error cargando eventos:", err);
      setError("No se pudieron cargar los eventos");
    } finally {
      setLoading(false);
    }
  };

  const mostrarAlerta = (mensaje, tipo = "success") => {
    setAlerta({ mostrar: true, mensaje, tipo });
    setTimeout(() => {
      setAlerta({ mostrar: false, mensaje: "", tipo: "success" });
    }, 3000);
  };

  // ==================== DRAG & DROP ====================
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    } else {
      mostrarAlerta("Solo se permiten imágenes", "danger");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const processFile = (file) => {
    if (file.size > 5 * 1024 * 1024) {
      mostrarAlerta("La imagen no debe superar los 5MB", "danger");
      return;
    }
    setImagenFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagenPreview(previewUrl);
  };

  const removeImagen = () => {
    setImagenFile(null);
    if (imagenPreview) URL.revokeObjectURL(imagenPreview);
    setImagenPreview(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetFormulario = () => {
    setFormData({
      titulo: "",
      descripcion: "",
      tipo_evento: "",
      url_pdf: "",
      url_externa: "",
    });
    setImagenFile(null);
    if (imagenPreview) URL.revokeObjectURL(imagenPreview);
    setImagenPreview(null);
    setEsEdicion(false);
    setMostrarFormulario(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.titulo.trim() || !formData.tipo_evento) {
      mostrarAlerta("Título y tipo de evento son obligatorios", "danger");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("titulo", formData.titulo.trim());
    formDataToSend.append("descripcion", formData.descripcion.trim());
    formDataToSend.append("tipo_evento", formData.tipo_evento);

    if (formData.tipo_evento === "PRINCIPAL") {
      formDataToSend.append("url_pdf", formData.url_pdf);
    }
    if (formData.tipo_evento === "PROMOCIONAL") {
      formDataToSend.append("url_externa", formData.url_externa);
    }

    if (imagenFile) {
      formDataToSend.append("imagen_portada", imagenFile);
    }

    try {
      if (esEdicion) {
        await axios.put(`/eventos/${formData.id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        mostrarAlerta("Evento actualizado correctamente");
      } else {
        await axios.post("/eventos", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        mostrarAlerta("Evento creado exitosamente");
      }

      resetFormulario();
      cargarEventos();
    } catch (err) {
      console.error("Error en submit:", err);
      mostrarAlerta(
        err.response?.data?.msg || "Error al guardar el evento",
        "danger"
      );
    }
  };

  const iniciarEdicion = (evento) => {
    setFormData({
      id: evento.id,
      titulo: evento.titulo || "",
      descripcion: evento.descripcion || "",
      tipo_evento: evento.tipo_evento || "",
      url_pdf: evento.url_pdf || "",
      url_externa: evento.url_externa || "",
    });
    setEsEdicion(true);
    setImagenFile(null);
    setImagenPreview(evento.imagen_portada || null);
    setMostrarFormulario(true);
  };

  const confirmarEliminar = (id, titulo) => {
    setModalEliminar({ mostrar: true, id, titulo });
  };

  const eliminarEvento = async () => {
    try {
      await axios.delete(`/eventos/${modalEliminar.id}`);
      setModalEliminar({ mostrar: false, id: null, titulo: "" });
      mostrarAlerta("Evento eliminado correctamente");
      cargarEventos();
    } catch (err) {
      console.error("Error eliminando evento:", err);
      mostrarAlerta(
        err.response?.data?.msg || "No se pudo eliminar el evento",
        "danger"
      );
    }
  };

  return (
    <div className="container-fluid bg-light min-vh-100 py-4 px-lg-5">

      {/* Alerta flotante */}
      {alerta.mostrar && (
        <div
          className={`alert alert-${alerta.tipo} alert-dismissible fade show shadow position-fixed`}
          style={{ top: "20px", right: "20px", zIndex: 9999, minWidth: "320px" }}
        >
          <div className="d-flex align-items-center">
            <i className={`bi me-2 ${alerta.tipo === "success" ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
            <span>{alerta.mensaje}</span>
          </div>
          <button className="btn-close" onClick={() => setAlerta({ ...alerta, mostrar: false })}></button>
        </div>
      )}

      {/* Header */}
      <div className="card shadow-sm border-0 mb-4 border-start border-success border-4">
        <div className="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-outline-success border-0 rounded-circle p-2"
              onClick={() => navigate("/DashboardSecretaria")}
            >
              <i className="bi bi-arrow-left-short fs-4"></i>
            </button>
            <h4 className="fw-bold text-dark mb-0">
              <i className="bi bi-calendar-event me-2 text-success"></i>
              Gestión de Eventos
            </h4>
          </div>
          <button
            className="btn btn-success"
            onClick={() => {
              resetFormulario();
              setMostrarFormulario(true);
            }}
            disabled={mostrarFormulario}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Nuevo Evento
          </button>
        </div>
      </div>

      {/* Formulario */}
      {mostrarFormulario && (
        <div className="card shadow-sm border-0 mb-5">
          <div className="card-body">
            <h5 className="fw-bold mb-4 text-success">
              {esEdicion ? "Editar Evento" : "Crear Nuevo Evento"}
            </h5>

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label fw-bold small">Título *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="titulo"
                    value={formData.titulo}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-bold small">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-bold small">Tipo de Evento *</label>
                  <select
                    className="form-select"
                    name="tipo_evento"
                    value={formData.tipo_evento}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleccione tipo</option>
                    <option value="PRINCIPAL">Principal</option>
                    <option value="PROMOCIONAL">Promocional</option>
                    <option value="INFORMATIVO">Informativo</option>
                  </select>
                </div>

                {formData.tipo_evento === "PRINCIPAL" && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold small">URL del PDF *</label>
                    <input
                      type="url"
                      className="form-control"
                      name="url_pdf"
                      value={formData.url_pdf}
                      onChange={handleChange}
                      required
                      placeholder="https://..."
                    />
                  </div>
                )}

                {formData.tipo_evento === "PROMOCIONAL" && (
                  <div className="col-md-6">
                    <label className="form-label fw-bold small">URL Externa *</label>
                    <input
                      type="url"
                      className="form-control"
                      name="url_externa"
                      value={formData.url_externa}
                      onChange={handleChange}
                      required
                      placeholder="https://..."
                    />
                  </div>
                )}

                {/* ==================== DRAG & DROP IMAGEN ==================== */}
                <div className="col-md-12">
                  <label className="form-label fw-bold small">Imagen de Portada</label>
                  <div
                    className={`border border-2 border-dashed rounded-3 p-4 text-center ${
                      isDragging ? "border-success bg-success bg-opacity-10" : "border-secondary"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById("imagenPortadaInput").click()}
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="file"
                      id="imagenPortadaInput"
                      accept="image/*"
                      className="d-none"
                      onChange={handleFileSelect}
                    />

                    {imagenPreview ? (
                      <div className="position-relative d-inline-block">
                        <img
                          src={imagenPreview}
                          alt="Vista previa"
                          className="img-fluid rounded shadow-sm"
                          style={{ maxHeight: "200px", objectFit: "contain" }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                          onClick={removeImagen}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ) : (
                      <div>
                        <i className="bi bi-cloud-upload fs-1 text-muted mb-2 d-block"></i>
                        <p className="mb-1 fw-medium">Arrastra la imagen aquí o haz clic para seleccionar</p>
                        <small className="text-muted">PNG, JPG, WEBP (máx. 5MB)</small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 text-end mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={resetFormulario}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-success px-4">
                    {esEdicion ? "Actualizar Evento" : "Crear Evento"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de eventos */}
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h5 className="fw-bold mb-4">Eventos Registrados</h5>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : eventos.length === 0 ? (
            <div className="alert alert-info text-center">
              No hay eventos registrados aún
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Portada</th>
                    <th>Título</th>
                    <th>Tipo</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {eventos.map((evento) => (
                    <tr key={evento.id}>
                      <td>
                        {evento.imagen_portada ? (
                          <img
                            src={evento.imagen_portada}
                            alt={evento.titulo}
                            style={{ width: "60px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        ) : (
                          <span className="text-muted">Sin imagen</span>
                        )}
                      </td>
                      <td className="fw-semibold">{evento.titulo}</td>
                      <td>
                        <span
                          className={`badge ${
                            evento.tipo_evento === "PRINCIPAL"
                              ? "bg-primary"
                              : evento.tipo_evento === "PROMOCIONAL"
                              ? "bg-warning text-dark"
                              : "bg-info"
                          }`}
                        >
                          {evento.tipo_evento}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {new Date(evento.created_at).toLocaleDateString("es-ES")}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => iniciarEdicion(evento)}
                        >
                          <i className="bi bi-pencil"></i> Editar
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => confirmarEliminar(evento.id, evento.titulo)}
                        >
                          <i className="bi bi-trash"></i> Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación para eliminar */}
      {modalEliminar.mostrar && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "2rem",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "1rem" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#fff0f0",
                  marginBottom: "1rem",
                }}
              >
                <i className="bi bi-trash3" style={{ fontSize: 26, color: "#dc3545" }}></i>
              </span>
              <h5 style={{ fontWeight: 600, marginBottom: "0.5rem" }}>¿Eliminar evento?</h5>
              <p style={{ color: "#6c757d", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                Estás a punto de eliminar <strong>"{modalEliminar.titulo}"</strong>.<br />
                Esta acción no se puede deshacer.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button
                className="btn btn-light w-100"
                onClick={() => setModalEliminar({ mostrar: false, id: null, titulo: "" })}
              >
                Cancelar
              </button>
              <button className="btn btn-danger w-100" onClick={eliminarEvento}>
                <i className="bi bi-trash me-1"></i> Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}